// Globalny silnik minutników: jeden tick + alarm na poziomie aplikacji, dzięki
// czemu odliczanie i dzwonienie działają niezależnie od aktualnej podstrony.
// Stan współdzielony przez useState; interwał jako singleton modułu,
// a odtwarzanie dźwięku w kanale 'timers' z useAlarmSound.

import { ALARM_SOUNDS, alarmSoundSrc, useAlarmSound } from '~/composables/useAlarmSound'

export interface AppTimer {
    storageKey: string
    name: string
    inputMinutes: number
    inputSeconds: number
    soundKey: string
    mode: 'idle' | 'running' | 'paused' | 'finished'
    configuredSeconds: number
    endTime: number
    remainingMs: number
}

export const TIMER_SOUNDS = ALARM_SOUNDS

const TIMER_KEYS = ['rountine.timer.1', 'rountine.timer.2', 'rountine.timer.3']

const newTimer = (storageKey: string): AppTimer => ({
    storageKey,
    name: '',
    inputMinutes: 5,
    inputSeconds: 0,
    soundKey: 'alarm_1',
    mode: 'idle',
    configuredSeconds: 5 * 60,
    endTime: 0,
    remainingMs: 0,
})

// --- singletony modułu (żyją przez cały czas życia SPA) ---
let intervalId: any = null
let engineStarted = false

export const useTimers = () => {
    const timers = useState<AppTimer[]>('timers.list', () => TIMER_KEYS.map(newTimer))
    const now = useState<number>('timers.now', () => Date.now())

    // Alarm gra dopóki którykolwiek minutnik jest w stanie "finished".
    const anyFinished = () => timers.value.some(t => t.mode === 'finished')
    const alarm = useAlarmSound('timers', anyFinished)

    const soundSrc = alarmSoundSrc

    // --- persystencja ---
    const save = (t: AppTimer) => {
        if (!import.meta.client) return
        try {
            localStorage.setItem(t.storageKey, JSON.stringify({
                name: t.name,
                minutes: Number(t.inputMinutes) || 0,
                seconds: Number(t.inputSeconds) || 0,
                sound: t.soundKey,
                mode: t.mode,
                configuredSeconds: t.configuredSeconds,
                endTime: t.endTime,
                remainingMs: t.remainingMs,
            }))
        } catch { /* brak miejsca / tryb prywatny */ }
    }

    const load = (t: AppTimer) => {
        if (!import.meta.client) return
        try {
            const raw = localStorage.getItem(t.storageKey)
            if (!raw) return
            const d = JSON.parse(raw)
            if (typeof d.name === 'string') t.name = d.name
            if (Number.isFinite(d.minutes)) t.inputMinutes = d.minutes
            if (Number.isFinite(d.seconds)) t.inputSeconds = d.seconds
            if (typeof d.sound === 'string' && TIMER_SOUNDS.some(s => s.key === d.sound)) t.soundKey = d.sound
            if (Number.isFinite(d.configuredSeconds)) t.configuredSeconds = d.configuredSeconds
            if (Number.isFinite(d.endTime)) t.endTime = d.endTime

            if (d.mode === 'running' && Number.isFinite(d.endTime)) {
                t.endTime = d.endTime
                if (Date.now() >= d.endTime) finish(t)   // czas minął, gdy app była zamknięta
                else t.mode = 'running'
            } else if (d.mode === 'paused' && Number.isFinite(d.remainingMs)) {
                t.remainingMs = d.remainingMs
                t.mode = 'paused'
            } else if (d.mode === 'finished') {
                finish(t)
            }
        } catch { /* uszkodzony wpis */ }
    }

    // --- audio / alarm ---
    // Zapętlony sygnał narasta w czasie — obsługa w useAlarmSound.
    const startAlarm = (src: string) => alarm.play(src, true)

    const stopAlarmIfNoneFinished = () => {
        if (!anyFinished()) alarm.stop()
    }

    const stopTestSound = () => alarm.stopTest()

    const testSound = (soundKey: string) => alarm.playTest(soundSrc(soundKey))

    // --- przejścia stanu ---
    const finish = (t: AppTimer) => {
        t.mode = 'finished'
        save(t)
        startAlarm(soundSrc(t.soundKey))
    }

    const syncConfigured = (t: AppTimer) => {
        const m = Math.max(0, Math.floor(Number(t.inputMinutes) || 0))
        const s = Math.min(59, Math.max(0, Math.floor(Number(t.inputSeconds) || 0)))
        t.inputMinutes = m
        t.inputSeconds = s
        t.configuredSeconds = m * 60 + s
    }

    const start = (t: AppTimer) => {
        syncConfigured(t)
        if (t.configuredSeconds <= 0) return
        t.endTime = Date.now() + t.configuredSeconds * 1000
        t.mode = 'running'
        save(t)
    }
    const pause = (t: AppTimer) => {
        if (t.mode !== 'running') return
        t.remainingMs = Math.max(0, t.endTime - Date.now())
        t.mode = 'paused'
        save(t)
    }
    const resume = (t: AppTimer) => {
        if (t.mode !== 'paused') return
        t.endTime = Date.now() + t.remainingMs
        t.mode = 'running'
        save(t)
    }
    const reset = (t: AppTimer) => {
        t.mode = 'idle'
        save(t)
        stopAlarmIfNoneFinished()
    }
    const stopAndReset = (t: AppTimer) => {
        t.mode = 'idle'
        save(t)
        stopAlarmIfNoneFinished()
    }
    const restart = (t: AppTimer) => {
        t.mode = 'idle'
        stopAlarmIfNoneFinished()
        start(t)
    }
    const applyPreset = (t: AppTimer, totalSeconds: number) => {
        t.inputMinutes = Math.floor(totalSeconds / 60)
        t.inputSeconds = totalSeconds % 60
        t.configuredSeconds = totalSeconds
        save(t)
    }
    const selectSound = (t: AppTimer, soundKey: string) => {
        t.soundKey = soundKey
        stopTestSound()
        save(t)
    }
    const setName = (t: AppTimer, name: string) => { t.name = name; if (t.mode === 'idle') save(t) }

    // --- wartości pochodne (per timer) ---
    const remainingSeconds = (t: AppTimer): number => {
        if (t.mode === 'idle') return t.configuredSeconds
        if (t.mode === 'finished') return 0
        if (t.mode === 'paused') return Math.max(0, Math.ceil(t.remainingMs / 1000))
        return Math.max(0, Math.ceil((t.endTime - now.value) / 1000))
    }
    const overtimeSeconds = (t: AppTimer): number =>
        t.mode === 'finished' ? Math.max(0, Math.floor((now.value - t.endTime) / 1000)) : 0

    // --- globalny silnik (jeden na całą aplikację) ---
    const ensureEngine = () => {
        if (!import.meta.client || engineStarted) return
        engineStarted = true
        timers.value.forEach(load)
        intervalId = setInterval(() => {
            now.value = Date.now()
            for (const t of timers.value) {
                if (t.mode === 'running' && now.value >= t.endTime) finish(t)
            }
        }, 250)
    }

    // --- agregaty dla wskaźnika w nagłówku ---
    const finishedCount = computed(() => timers.value.filter(t => t.mode === 'finished').length)
    const runningCount = computed(() => timers.value.filter(t => t.mode === 'running').length)
    const pausedCount = computed(() => timers.value.filter(t => t.mode === 'paused').length)
    const activeCount = computed(() => timers.value.filter(t => t.mode !== 'idle').length)
    const nearestRunningSeconds = computed(() => {
        const running = timers.value.filter(t => t.mode === 'running').map(remainingSeconds)
        return running.length ? Math.min(...running) : null
    })

    return {
        timers, now, sounds: TIMER_SOUNDS,
        ensureEngine, save,
        start, pause, resume, reset, stopAndReset, restart,
        applyPreset, selectSound, setName, syncConfigured, testSound,
        remainingSeconds, overtimeSeconds,
        finishedCount, runningCount, pausedCount, activeCount, nearestRunningSeconds,
    }
}
